import { Component } from 'react';
import { AlertTriangle, RotateCcw, LayoutDashboard } from 'lucide-react';

/**
 * App-wide error boundary.
 *
 * Prevents a single render-time exception from unmounting the whole React tree
 * and leaving the user staring at a blank/dark screen. Instead we show a calm,
 * on-brand recovery card with a way back into the app.
 *
 * Recovery uses `window.location` (not the router) so it works even if the crash
 * originated from inside the router/route tree.
 */
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch() {
    // Intentionally silent: the fallback UI is the user-facing recovery path.
  }

  handleReload = () => {
    window.location.reload();
  };

  handleHome = () => {
    window.location.assign('/dashboard');
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        data-testid="app-error-boundary"
        className="app-canvas flex min-h-screen items-center justify-center px-4"
      >
        <div className="glass-card w-full max-w-[420px] rounded-2xl p-6 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-400/15 text-amber-300">
            <AlertTriangle className="h-6 w-6" />
          </div>
          <h1 className="font-display text-xl font-semibold text-foreground">
            Something needs a quick refresh
          </h1>
          <p className="mt-2 text-sm leading-6 text-foreground/65">
            The screen ran into an unexpected hiccup. Your data is safe — let's get you back on track.
          </p>
          <div className="mt-6 flex flex-col gap-2.5">
            <button
              type="button"
              data-testid="error-boundary-reload-button"
              onClick={this.handleReload}
              className="btn-brand flex h-11 w-full items-center justify-center gap-2 rounded-xl text-sm font-semibold text-brand-ink"
            >
              <RotateCcw className="h-4 w-4" /> Reload
            </button>
            <button
              type="button"
              data-testid="error-boundary-home-button"
              onClick={this.handleHome}
              className="flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.05] text-sm font-medium text-foreground/80 transition-colors hover:bg-white/[0.1]"
            >
              <LayoutDashboard className="h-4 w-4" /> Go to dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }
}
